import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../mock';
import ChartCard from '../components/ChartCard';
import DataTable from '../components/DataTable';
import PeriodFilter from '../components/PeriodFilter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { subDays, isAfter, isBefore, parseISO } from 'date-fns';
import { Battery, Users } from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const Carregamento = () => {
  const navigate = useNavigate();
  const [periodFilter, setPeriodFilter] = useState({ type: 'preset', days: 30 });

  const filteredData = useMemo(() => {
    if (periodFilter.type === 'preset') {
      const cutoffDate = subDays(new Date(), periodFilter.days);
      return mockData.carregamento.filter(item => {
        const itemDate = parseISO(item.data);
        return isAfter(itemDate, cutoffDate);
      });
    } else {
      return mockData.carregamento.filter(item => {
        const itemDate = parseISO(item.data);
        return isAfter(itemDate, periodFilter.startDate) && isBefore(itemDate, periodFilter.endDate);
      });
    }
  }, [periodFilter]);

  const chartData = useMemo(() => {
    const grouped = {};
    filteredData.forEach(item => {
      if (!grouped[item.data]) {
        grouped[item.data] = { data: item.data, consumo: 0, sessoes: 0 };
      }
      grouped[item.data].consumo += parseFloat(item.consumo);
      grouped[item.data].sessoes += 1;
    });
    return Object.values(grouped).map(item => ({
      ...item,
      consumo: parseFloat(item.consumo.toFixed(2))
    }));
  }, [filteredData]);

  const siteStats = useMemo(() => {
    const sites = ['Pragal', 'Santa Apolónia', 'Évora', 'Freixo'];
    return sites.map(site => {
      const siteRecords = filteredData.filter(item => item.local === site);
      const total = siteRecords.reduce((sum, item) => sum + parseFloat(item.consumo), 0);
      return {
        local: site,
        total: parseFloat(total.toFixed(2)),
        sessoes: siteRecords.length
      };
    });
  }, [filteredData]);

  const userStats = useMemo(() => {
    const users = {};
    filteredData.forEach(item => {
      if (!users[item.utilizador]) {
        users[item.utilizador] = { utilizador: item.utilizador, consumo: 0, sessoes: 0 };
      }
      users[item.utilizador].consumo += parseFloat(item.consumo);
      users[item.utilizador].sessoes += 1;
    });
    return Object.values(users).map(u => ({
      ...u,
      consumo: parseFloat(u.consumo.toFixed(2))
    })).sort((a, b) => b.consumo - a.consumo).slice(0, 5);
  }, [filteredData]);

  const columns = [
    { key: 'data', label: 'Data' },
    { key: 'local', label: 'Local' },
    { key: 'posto', label: 'Posto' },
    { key: 'horaInicio', label: 'Hora Início' },
    { key: 'horaFim', label: 'Hora Fim' },
    { key: 'consumo', label: 'Consumo (kWh)', format: (val) => `${val} kWh` },
    { key: 'utilizador', label: 'Utilizador' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Postos de Carregamento VE</h1>
          <p className="text-gray-600 mt-1">Consumo dos veículos elétricos por local e utilizador</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/carregamento/postos')}>
            <Battery className="h-4 w-4 mr-2" />
            Gerir Postos
          </Button>
          <Button variant="outline" onClick={() => navigate('/carregamento/utilizadores')}>
            <Users className="h-4 w-4 mr-2" />
            Gerir Utilizadores
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <PeriodFilter 
          value={periodFilter.type === 'preset' ? periodFilter.days.toString() : 'custom'}
          onChange={setPeriodFilter}
        />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {siteStats.map((site, idx) => (
          <div key={site.local} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">{site.local}</h3>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }}></div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Consumo:</span>
                <span className="text-sm font-bold text-blue-600">{site.total.toLocaleString('pt-PT')} kWh</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Sessões:</span>
                <span className="text-sm font-semibold text-gray-700">{site.sessoes}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title={`Consumo Diário (${chartData.length} dias)`}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="data" tick={{ fontSize: 10 }} stroke="#6b7280" />
              <YAxis tick={{ fontSize: 11 }} stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="consumo" fill="#3b82f6" name="Consumo (kWh)" />
              <Bar dataKey="sessoes" fill="#10b981" name="Sessões" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Distribuição por Local">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={siteStats}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ local, total }) => `${local}: ${total} kWh`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="total"
              >
                {siteStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top Users */}
      <ChartCard title="Top 5 Utilizadores">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={userStats} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" tick={{ fontSize: 11 }} stroke="#6b7280" />
            <YAxis dataKey="utilizador" type="category" tick={{ fontSize: 11 }} stroke="#6b7280" width={120} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Bar dataKey="consumo" fill="#3b82f6" name="Consumo (kWh)" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Data Table */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Histórico de Sessões de Carregamento</h2>
        <DataTable
          data={filteredData}
          columns={columns}
          title="carregamento-ve"
        />
      </div>
    </div>
  );
};

export default Carregamento;
